import { useMemo } from "react";
import { FloatingPortal } from "@floating-ui/react";
import { CheckIcon } from "@heroicons/react/20/solid";
import { usePopover } from "../hooks/usePopover";
import { classNames } from "../utils";
import { playbackRateOptions } from "../videojsUtils";

// Rates shown in the dropdown. Separate from `playbackRateOptions` (which is
// the decimals lookup table) because the legacy player listed these specific
// values in the menu — preserving that exactly.
const RATE_CHOICES = [
  0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 3, 4, 6, 8,
] as const;

// Always show at least one decimal place ("1.0x" instead of "1x") per Figma.
// Fractional rates use as many decimals as needed (0.25 -> "0.25x", 1.25 -> "1.25x").
function formatRateLabel(rate: number): string {
  return `${Number.isInteger(rate) ? rate.toFixed(1) : String(rate)}x`;
}

export interface PlaybackRateMenuProps {
  /** Persisted "natural" playback rate. */
  playbackRate: number;
  /** Current rate including JKL temp scrub speeds. Equal to playbackRate when no temp is active. */
  tempPlaybackRate: number;
  /** Set the rate. `persist=true` writes through to settings; false is a temp scrub. */
  setRate: (rate: number, persist: boolean) => void;
}

/**
 * Shows the rate menu trigger when no temp scrub is active. When a JKL ramp is
 * in flight (temp rate differs from natural), shows a yellow indicator with
 * the current temp value instead.
 */
export function PlaybackRateMenu({
  playbackRate,
  tempPlaybackRate,
  setRate,
}: PlaybackRateMenuProps) {
  const playbackRateOption = useMemo(
    () =>
      playbackRateOptions.find((o) => o.rate === playbackRate) ?? {
        rate: playbackRate,
        decimals: Number.isInteger(Math.abs(playbackRate))
          ? 0
          : Math.abs(playbackRate) % 1 === 0.5
            ? 1
            : 2,
      },
    [playbackRate],
  );

  const showingTempScrub =
    !!tempPlaybackRate && tempPlaybackRate !== playbackRate;

  if (showingTempScrub) {
    return (
      <span
        title="Playback rate"
        className="flex shrink-0 select-none items-center text-sm"
      >
        <div className="flex w-6 justify-center text-yellow-500">
          {tempPlaybackRate.toFixed(
            tempPlaybackRate === -playbackRate
              ? playbackRateOption.decimals
              : 1,
          )}
          x
        </div>
      </span>
    );
  }

  return (
    <RatePopover
      currentRate={playbackRateOption.rate}
      onSelect={(rate) => setRate(rate, true)}
    />
  );
}

interface RatePopoverProps {
  currentRate: number;
  onSelect: (rate: number) => void;
}

function RatePopover({ currentRate, onSelect }: RatePopoverProps) {
  const {
    isOpen,
    setIsOpen,
    refs,
    floatingStyles,
    getReferenceProps,
    getFloatingProps,
  } = usePopover({ placement: "top" });

  return (
    <>
      <button
        type="button"
        ref={refs.setReference}
        {...getReferenceProps()}
        title="Playback rate"
        aria-label="Playback rate"
        className={classNames(
          "flex shrink-0 cursor-pointer select-none items-center rounded px-1 text-sm text-[#282A30] transition-colors hover:bg-[#F7F8F9]",
          isOpen && "bg-[#F7F8F9]",
        )}
      >
        {formatRateLabel(currentRate)}
      </button>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-50 max-h-72 w-28 overflow-y-auto rounded-lg border border-[#DFE1E4] bg-white p-1 text-[13px] text-[#282A30] shadow-[0_8px_28px_-4px_rgba(0,0,0,0.18),_0_0_0_0.5px_rgba(0,0,0,0.06)]"
          >
            {RATE_CHOICES.map((rate) => {
              const selected = rate === currentRate;
              return (
                <button
                  key={rate}
                  type="button"
                  onClick={() => {
                    onSelect(rate);
                    setIsOpen(false);
                  }}
                  className={classNames(
                    "flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left transition-colors hover:bg-[#F7F8F9]",
                    selected && "bg-[#F7F8F9]",
                  )}
                  role="menuitemradio"
                  aria-checked={selected}
                >
                  <span>{formatRateLabel(rate)}</span>
                  {selected && (
                    <CheckIcon className="h-3.5 w-3.5 text-[#6526DB]" />
                  )}
                </button>
              );
            })}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
